// src/Pages/BranchForm.jsx
import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Grid,
  Switch,
  FormControlLabel,
  Button,
  Typography,
  IconButton,
  Paper,
  Chip,
  Stack,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import Avatar from "@mui/material/Avatar";
import { useSnackbar } from "../Snackbar/SnackbarContext";

const baseForm = {
  name: "",
  address: "",
  city: "",
  state: "",
  country: "",
  pincode: "",
  email: "",
  mobile: "",
  phone: "",
  logo: "",
  gstIn: "",
  isActive: 1,
  createdOn: new Date().toISOString(),
  modifiedOn: new Date().toISOString(),
};

const BranchForm = ({ initialData, viewOnly = false, onSubmit, onClose }) => {
  const [form, setForm] = useState(baseForm);
  const [errors, setErrors] = useState({});
  const { showSnackbar } = useSnackbar();
  // ⭐ ADDED — Validation helper
  const validateForm = (data) => {
    const newErrors = {};

    if (!data.name.trim()) newErrors.name = "Branch name is required";
    if (!data.address.trim()) newErrors.address = "Address is required";
    if (!data.city.trim()) newErrors.city = "City is required";
    if (!data.state.trim()) newErrors.state = "State is required";
    if (!data.country.trim()) newErrors.country = "Country is required";

    if (!data.pincode.trim()) {
      newErrors.pincode = "Pincode is required";
    } else if (!/^\d{6}$/.test(data.pincode)) {
      newErrors.pincode = "Pincode must be 6 digits";
    }

    if (data.email && !/^\S+@\S+\.\S+$/.test(data.email)) {
      newErrors.email = "Enter a valid email";
    }

    if (data.mobile && !/^\d{10}$/.test(data.mobile)) {
      newErrors.mobile = "Mobile must be 10 digits";
    }

    if (!data.logo) newErrors.logo = "Branch logo is required";

    return newErrors;
  };

  useEffect(() => {
    if (initialData) {
      setForm(initialData);
    } else {
      setForm(baseForm);
    }
  }, [initialData]);

  // ⭐ NEW — handles image upload (base64 stored in form.logo)
  const handleLogoUpload = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      handleChange("logo", reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleChange = (key, value) => {
    if (viewOnly) return;
    setForm((prev) => ({
      ...prev,
      [key]: value,
      modifiedOn: new Date().toISOString(),
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (viewOnly) return;

    const validationErrors = validateForm(form);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      showSnackbar("Please fill all required fields", "error"); // ⭐ new
      return; // ❌ stop submit
    }

    onSubmit(form); // ✅ valid
    showSnackbar(
      initialData
        ? "Branch updated successfully!"
        : "Branch created successfully!",
      "success"
    );
  };

  return (
    <>
      {/* FULL SCREEN OVERLAY */}
      <Box
        sx={{
          position: "fixed",
          inset: 0,
          zIndex: 2000,
          bgcolor: "rgba(15,23,42,0.45)",
          backdropFilter: "blur(6px)",
          display: "flex",
          justifyContent: "flex-end",
        }}
        onClick={onClose}
      >
        {/* RIGHT SIDE PANEL */}
        <Box
          sx={{
            width: { xs: "100%", sm: "80%", md: "55%" },
            height: "100vh",
            p: { xs: 2, md: 3 },
            boxSizing: "border-box",
            display: "flex",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Paper
            elevation={6}
            sx={{
              position: "relative",
              width: "100%",
              height: "100%",
              borderRadius: 4,
              p: { xs: 3, md: 4 },
              bgcolor: "rgba(255,255,255,0.96)",
              backdropFilter: "blur(18px)",
              boxShadow:
                "0 18px 45px rgba(15, 23, 42, 0.35), 0 0 0 1px rgba(148, 163, 184, 0.18)",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              animation: "slideIn 0.35s ease",
            }}
          >
            {/* CLOSE BUTTON */}
            <IconButton
              onClick={onClose}
              sx={{
                position: "absolute",
                top: 16,
                right: 16,
                bgcolor: "rgba(15,23,42,0.04)",
                "&:hover": {
                  bgcolor: "rgba(15,23,42,0.08)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>

            {/* HEADER */}
            <Box
              sx={{
                mb: 3,
                pr: 5,
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                alignItems: { xs: "flex-start", md: "center" },
                justifyContent: "space-between",
                gap: 1.5,
              }}
            >
              <Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    letterSpacing: 0.3,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  🏫{" "}
                  {viewOnly
                    ? "Branch Details"
                    : initialData
                    ? "Edit Branch"
                    : "Add New Branch"}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", mt: 0.5 }}
                >
                  Manage your school branches. Fields marked with * are
                  required.
                </Typography>
              </Box>

              <Chip
                label={form.isActive ? "Active Branch" : "Inactive Branch"}
                variant="outlined"
                sx={{
                  borderRadius: "999px",
                  fontSize: 12,
                  fontWeight: 500,
                  borderStyle: "dashed",
                  bgcolor: form.isActive
                    ? "rgba(22,163,74,0.06)"
                    : "rgba(148,163,184,0.06)",
                  borderColor: form.isActive
                    ? "rgba(22,163,74,0.4)"
                    : "rgba(148,163,184,0.6)",
                  color: form.isActive ? "success.main" : "text.secondary",
                }}
              />
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* CONTENT SCROLL AREA */}
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{
                flex: 1,
                overflowY: "auto",
                pr: 1,
              }}
            >
              <Grid container spacing={3}>
                {/* LEFT SIDE - MAIN DETAILS */}
                <Grid item xs={12} md={7}>
                  <Grid container spacing={2.5} mt={0.2}>
                    {/* Branch Name */}
                    <Grid item xs={12}>
                      <TextField
                        label="Branch Name *"
                        value={form.name || ""}
                        onChange={(e) => handleChange("name", e.target.value)}
                        fullWidth
                        disabled={viewOnly}
                        error={!!errors.name}
                        helperText={errors.name}
                        placeholder="e.g. Montessori Central Campus"
                        InputProps={{ sx: { borderRadius: 3 } }}
                      />
                    </Grid>

                    {/* Address */}
                    <Grid item xs={12}>
                      <TextField
                        label="Address *"
                        value={form.address || ""}
                        onChange={(e) =>
                          handleChange("address", e.target.value)
                        }
                        fullWidth
                        disabled={viewOnly}
                        multiline
                        minRows={2}
                        error={!!errors.address}
                        helperText={errors.address}
                        placeholder="Street, area, landmark"
                        InputProps={{ sx: { borderRadius: 3 } }}
                      />
                    </Grid>

                    {/* City & State */}
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="City *"
                        value={form.city || ""}
                        onChange={(e) => handleChange("city", e.target.value)}
                        fullWidth
                        disabled={viewOnly}
                        error={!!errors.city}
                        helperText={errors.city}
                        placeholder="e.g. Kochi"
                        InputProps={{ sx: { borderRadius: 3 } }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        label="State *"
                        value={form.state || ""}
                        onChange={(e) => handleChange("state", e.target.value)}
                        fullWidth
                        disabled={viewOnly}
                        error={!!errors.state}
                        helperText={errors.state}
                        placeholder="e.g. Kerala"
                        InputProps={{
                          sx: { borderRadius: 3 },
                        }}
                      />
                    </Grid>

                    {/* Country & Pincode */}
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Country *"
                        value={form.country || ""}
                        onChange={(e) =>
                          handleChange("country", e.target.value)
                        }
                        fullWidth
                        disabled={viewOnly}
                        error={!!errors.country}
                        helperText={errors.country}
                        placeholder="e.g. India"
                        InputProps={{
                          sx: { borderRadius: 3 },
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        type="number"
                        label="Pincode *"
                        value={form.pincode || ""}
                        onChange={(e) =>
                          handleChange("pincode", e.target.value)
                        }
                        fullWidth
                        disabled={viewOnly}
                        error={!!errors.pincode}
                        helperText={errors.pincode}
                        placeholder="e.g. 682001"
                        InputProps={{
                          sx: { borderRadius: 3 },
                        }}
                      />
                    </Grid>

                    {/* Email & Mobile */}
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Email"
                        type="email"
                        value={form.email || ""}
                        onChange={(e) => handleChange("email", e.target.value)}
                        fullWidth
                        disabled={viewOnly}
                        error={!!errors.email}
                        helperText={errors.email}
                        placeholder="branch@school.edu"
                        InputProps={{
                          sx: { borderRadius: 3 },
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Mobile"
                        type="number"
                        value={form.mobile || ""}
                        onChange={(e) => handleChange("mobile", e.target.value)}
                        fullWidth
                        disabled={viewOnly}
                        error={!!errors.mobile}
                        helperText={errors.mobile}
                        placeholder="e.g. 9876543210"
                        InputProps={{
                          sx: { borderRadius: 3 },
                        }}
                      />
                    </Grid>

                    {/* Phone */}
                    <Grid item xs={12} md={6}>
                      <TextField
                        type="number"
                        label="Phone (Landline)"
                        value={form.phone || ""}
                        onChange={(e) => handleChange("phone", e.target.value)}
                        fullWidth
                        disabled={viewOnly}
                        error={!!errors.phone}
                        helperText={errors.phone}
                        placeholder="Optional landline number"
                        InputProps={{
                          sx: { borderRadius: 3 },
                        }}
                      />
                    </Grid>

                    {/* GSTIN */}
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="GSTIN"
                        value={form.gstIn || ""}
                        onChange={(e) => handleChange("gstIn", e.target.value)}
                        fullWidth
                        disabled={viewOnly}
                        error={!!errors.gstIn}
                        helperText={errors.gstIn}
                        placeholder="Branch GSTIN (if applicable)"
                        InputProps={{
                          sx: { borderRadius: 3 },
                        }}
                      />
                    </Grid>

                    {/* Active Switch */}
                    <Grid item xs={12} md={6}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={!!form.isActive}
                            onChange={(e) =>
                              handleChange("isActive", e.target.checked ? 1 : 0)
                            }
                            disabled={viewOnly}
                          />
                        }
                        label="Active Branch"
                      />
                    </Grid>
                  </Grid>
                </Grid>

                {/* RIGHT SIDE - META INFO */}
                <Grid item xs={12} md={5}>
                  <Box
                    sx={{
                      borderRadius: 4,
                      p: 2.5,
                      bgcolor: "rgba(15,23,42,0.03)",
                      border: "1px solid",
                      borderColor: "rgba(148,163,184,0.3)",
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                    }}
                  >
                    <Box
                      sx={{
                        borderRadius: 4,
                        p: 2.5,
                        bgcolor: "rgba(15,23,42,0.03)",
                        border: "1px solid rgba(148,163,184,0.3)",
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{ mb: 1.5, fontWeight: 600 }}
                      >
                        Branch Logo *
                      </Typography>
                      {errors.logo && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: "error.main",
                            mt: -1,
                            textAlign: "center",
                          }}
                        >
                          {errors.logo}
                        </Typography>
                      )}

                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: 2,
                        }}
                      >
                        {/* Circle Upload Area */}
                        <Box
                          sx={{
                            position: "relative",
                            width: 150,
                            height: 150,
                            borderRadius: "50%",
                            border: "2px dashed rgba(148,163,184,0.6)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: "rgba(255,255,255,0.9)",
                            overflow: "hidden",
                          }}
                        >
                          {form.logo ? (
                            <>
                              <Avatar
                                src={form.logo}
                                alt="Logo Preview"
                                sx={{
                                  width: "100%",
                                  height: "100%",
                                }}
                              />

                              {/* Remove Button */}
                              <IconButton
                                size="small"
                                onClick={() => handleChange("logo", "")}
                                sx={{
                                  position: "absolute",
                                  top: 6,
                                  right: 6,
                                  bgcolor: "rgba(15,23,42,0.7)",
                                  color: "#fff",
                                  "&:hover": {
                                    bgcolor: "rgba(15,23,42,0.9)",
                                  },
                                }}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </>
                          ) : (
                            <AddPhotoAlternateIcon
                              sx={{
                                fontSize: 42,
                                color: "rgba(148,163,184,0.9)",
                              }}
                            />
                          )}
                        </Box>

                        {/* Upload Button */}
                        {!viewOnly && (
                          <Button
                            variant="contained"
                            component="label"
                            sx={{
                              borderRadius: 999,
                              textTransform: "none",
                              px: 4,
                              fontWeight: 500,
                            }}
                          >
                            Upload Logo
                            <input
                              hidden
                              accept="image/*"
                              type="file"
                              onChange={(e) =>
                                handleLogoUpload(e.target.files[0])
                              }
                            />
                          </Button>
                        )}

                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            textAlign: "center",
                            mt: -1,
                          }}
                        >
                          Recommended: Square image at least 400×400px. PNG or
                          JPG only.
                        </Typography>
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        borderRadius: 3,
                        p: 1.5,
                        bgcolor: "rgba(79,70,229,0.04)",
                        border: "1px solid rgba(129,140,248,0.4)",
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          mb: 0.5,
                          fontSize: 13,
                          color: "primary.main",
                        }}
                      >
                        Branch Setup Tip
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary", lineHeight: 1.6 }}
                      >
                        Accurate branch details help in fee mapping, staff
                        allocation, attendance grouping, and reporting across
                        campuses.
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>

              {/* ACTION BUTTONS */}
              {!viewOnly && (
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.5}
                  justifyContent="flex-end"
                  sx={{ mt: 4, pb: 1 }}
                >
                  <Button
                    variant="outlined"
                    onClick={onClose}
                    sx={{
                      borderRadius: 999,
                      textTransform: "none",
                      px: 3,
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    sx={{
                      borderRadius: 999,
                      textTransform: "none",
                      px: 4,
                      fontWeight: 600,
                      boxShadow:
                        "0 10px 25px rgba(79,70,229,0.35), 0 0 0 1px rgba(129,140,248,0.5)",
                    }}
                  >
                    {initialData ? "Update Branch" : "Create Branch"}
                  </Button>
                </Stack>
              )}
            </Box>
          </Paper>
        </Box>
      </Box>

      {/* ANIMATION KEYFRAMES */}
      <style>
        {`
          @keyframes slideIn {
            from { transform: translateX(100%); opacity: 0; }
            to { transform: translateX(0); opacity: 1; }
          }
        `}
      </style>
    </>
  );
};

export default BranchForm;
